// EspaceProFondateurPage - Page Offre Membre Fondateur
// CONFORME À LA CHARTE : Hero identique aux autres pages Espace Pro + Vert Fondateur + Fond clair
// 100 premiers locataires - Ouverture 1er juillet

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Crown, Check, ArrowRight, X, Users, Building2, Wand2, Gift, 
  Phone, Globe, Share2, Bot, ShoppingBag, Shield, Calendar, 
  Percent, Sparkles, Star, ChevronRight, Eye, Lock,
  Zap, Award, Clock, BadgeCheck, Video, Rocket, UserCheck
} from 'lucide-react';
import ImmersiveEspaceProWrapper from '../../components/layout/ImmersiveEspaceProWrapper';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import EspaceProNav from '../../components/layout/EspaceProNav';

const CALENDLY_URL = 'https://calendly.com/abracadaworld/fondateur';

// Couleurs conformes à la charte
const C = {
  navy: '#1E3A5F',
  navyDeep: '#0F2744',
  navyDark: '#0a1628',
  orange: '#F97316',
  green: '#22C55E',
  greenDark: '#16a34a',
  greenLight: '#4ade80',
  cream: '#FFFBF7',
  gray50: '#F8FAFC',
  gray100: '#F1F5F9',
  gray200: '#E2E8F0',
  gray500: '#64748B',
  gray600: '#475569',
};

// Paramétrage inclus
const PARAMETRAGE = [
  { icon: Bot, text: 'Configuration de tous tes agents IA' },
  { icon: Share2, text: 'Connexion de tes réseaux sociaux' },
  { icon: ShoppingBag, text: 'Import de tes produits et prestations' },
  { icon: Globe, text: 'Création de ton site internet (Business & Premium)' },
  { icon: Wand2, text: 'Formation de ton Super Assistant à ton métier' },
  { icon: Phone, text: 'Support prioritaire pendant 30 jours' },
];

// Formules
const FORMULES = [
  {
    id: 'pro',
    name: 'Pro',
    pasDePorte: { normal: 500, fondateur: 250 },
    loyer: { normal: 50, fondateur: 25 },
    inclus: [
      'Boutique dans le centre commercial',
      'Super Assistant IA formé',
      'Vendeur IA dans ta boutique',
      'CRM complet',
      'Réseaux sociaux connectés',
    ],
    nonInclus: ['Site internet dédié'],
    highlight: false,
  },
  {
    id: 'business',
    name: 'Business',
    pasDePorte: { normal: 1000, fondateur: 500 },
    loyer: { normal: 100, fondateur: 50 },
    inclus: [
      'Tout de la formule Pro +',
      'Site internet à ton nom',
      '4 agents IA supplémentaires',
      'Multi-utilisateurs (équipe)',
      'Comptabilité IA intégrée',
    ],
    nonInclus: [],
    highlight: true,
    badge: 'POPULAIRE',
  },
  {
    id: 'premium',
    name: 'Premium',
    pasDePorte: { normal: 1500, fondateur: 750 },
    loyer: { normal: 150, fondateur: 75 },
    inclus: [
      'Tout de la formule Business +',
      'Site premium personnalisé',
      '8 agents IA supplémentaires',
      'Module paiement personnel',
      'Terminal NFC (téléphone)',
    ],
    nonInclus: [],
    highlight: false,
  },
];

// Styles globaux conformes à la charte
const GlobalStyles = () => (
  <style>{`
    .fondateur-page .hero {
      position: relative;
      overflow: hidden;
      isolation: isolate;
      background: linear-gradient(135deg, ${C.navyDeep} 0%, ${C.navy} 60%, ${C.navyDark} 100%);
      color: #fff;
      padding: 120px 24px 100px;
    }
    .fondateur-page .hero .inner {
      position: relative;
      z-index: 2;
      max-width: 1200px;
      margin: 0 auto;
    }
    .fondateur-page .hero .orb {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.45;
      z-index: 0;
    }
    .fondateur-page .hero .orb-1 {
      width: 520px;
      height: 520px;
      background: ${C.green};
      top: -160px;
      right: -120px;
      opacity: 0.25;
    }
    .fondateur-page .hero .orb-2 {
      width: 420px;
      height: 420px;
      background: ${C.navyDeep};
      bottom: -160px;
      left: -120px;
      opacity: 0.4;
    }
    .fondateur-page .hero .grid-tex {
      position: absolute;
      inset: 0;
      z-index: 1;
      opacity: 0.06;
      background-image:
        linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px);
      background-size: 56px 56px;
    }
    .fondateur-page .hero .display {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 800;
      font-size: clamp(42px, 6vw, 72px);
      line-height: 1.08;
      letter-spacing: -0.02em;
      color: #fff;
      text-align: center;
      max-width: 980px;
      margin: 0 auto;
    }
    .fondateur-page .hero .display .accent {
      color: ${C.green};
    }
    .fondateur-page .hero .lead {
      text-align: center;
      max-width: 640px;
      margin: 24px auto 0;
      color: rgba(255,255,255,0.82);
      font-size: 18px;
      line-height: 1.65;
    }
    .fondateur-page .hero .badge-row {
      display: flex;
      justify-content: center;
      margin-bottom: 28px;
    }
    .fondateur-page .hero .badge {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 10px 20px;
      background: rgba(34, 197, 94, 0.15);
      border: 1px solid rgba(34, 197, 94, 0.35);
      border-radius: 999px;
      color: ${C.greenLight};
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 0.03em;
    }
    .fondateur-page .hero .badge .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: ${C.green};
      box-shadow: 0 0 12px ${C.green};
      animation: pulse 2s ease-in-out infinite;
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.7; transform: scale(1.15); }
    }
    .fondateur-page .hero .stats {
      margin-top: 48px;
      display: flex;
      justify-content: center;
      gap: 48px;
      flex-wrap: wrap;
    }
    .fondateur-page .hero .stat {
      text-align: center;
    }
    .fondateur-page .hero .stat .v {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 800;
      font-size: clamp(32px, 4vw, 48px);
      line-height: 1;
    }
    .fondateur-page .hero .stat .v.orange { color: ${C.orange}; }
    .fondateur-page .hero .stat .v.green { color: ${C.green}; }
    .fondateur-page .hero .stat .l {
      margin-top: 8px;
      font-weight: 600;
      font-size: 13px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: rgba(255,255,255,0.7);
    }
    .fondateur-page .hero .ctas {
      margin-top: 40px;
      display: flex;
      justify-content: center;
      gap: 16px;
      flex-wrap: wrap;
    }
    .fondateur-page .hero .cta-primary {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 16px 32px;
      background: linear-gradient(135deg, ${C.green} 0%, ${C.greenDark} 100%);
      color: #fff;
      border-radius: 14px;
      font-weight: 700;
      font-size: 16px;
      text-decoration: none;
      box-shadow: 0 8px 28px rgba(34, 197, 94, 0.4);
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    .fondateur-page .hero .cta-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 36px rgba(34, 197, 94, 0.5);
    }
    .fondateur-page .hero .sub-info {
      margin-top: 20px;
      display: flex;
      justify-content: center;
      gap: 24px;
      flex-wrap: wrap;
      color: rgba(255,255,255,0.55);
      font-size: 13px;
    }
    .fondateur-page .hero .sub-info span {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    
    /* Sections claires */
    .fondateur-page .section-light {
      background: ${C.cream};
      padding: 80px 24px;
    }
    .fondateur-page .section-white {
      background: #fff;
      padding: 80px 24px;
    }
    .fondateur-page .container {
      max-width: 1100px;
      margin: 0 auto;
    }
    .fondateur-page .section-head {
      text-align: center;
      margin-bottom: 48px;
    }
    .fondateur-page .eyebrow {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      border-radius: 999px;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.04em;
      margin-bottom: 16px;
    }
    .fondateur-page .eyebrow-green {
      background: rgba(34, 197, 94, 0.12);
      border: 1px solid rgba(34, 197, 94, 0.25);
      color: ${C.greenDark};
    }
    .fondateur-page .eyebrow .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${C.green};
    }
    .fondateur-page .t-h2 {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 800;
      font-size: clamp(28px, 4vw, 42px);
      color: ${C.navy};
      line-height: 1.15;
      margin: 0;
    }
    .fondateur-page .t-h2 .accent {
      color: ${C.green};
    }
    .fondateur-page .t-lead {
      margin-top: 16px;
      font-size: 17px;
      color: ${C.gray500};
      line-height: 1.6;
    }
    
    @media (max-width: 768px) {
      .fondateur-page .hero {
        padding: 100px 20px 80px;
      }
      .fondateur-page .hero .stats {
        gap: 32px;
      }
      .fondateur-page .section-light,
      .fondateur-page .section-white {
        padding: 60px 20px;
      }
    }
  `}</style>
);

export default function EspaceProFondateurPage() {
  const [selectedPlan, setSelectedPlan] = useState('business');
  
  return (
    <ImmersiveEspaceProWrapper>
      <div className="fondateur-page ab-root">
        <GlobalStyles />
        <UnifiedHeader />
        <EspaceProNav />
        
        {/* ═══════════════════════════════════════════════════════════════════
            HERO — Identique aux autres pages Espace Pro
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="hero">
          {/* Orbs décoratifs verts */}
          <div className="orb orb-1" />
          <div className="orb orb-2" />
          <div className="grid-tex" />
          
          <div className="inner">
            {/* Badge urgence */}
            <motion.div 
              className="badge-row"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="badge" data-testid="urgency-badge">
                <span className="dot" />
                <Crown size={16} />
                Offre limitée aux 100 premiers — Plus que 97 places
              </div>
            </motion.div>
            
            {/* Titre */}
            <motion.h1 
              className="display"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Deviens Membre<br />
              <span className="accent">Fondateur</span>
            </motion.h1>
            
            {/* Sous-titre */}
            <motion.p 
              className="lead"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Loue ta boutique dans le <strong>1er centre commercial virtuel</strong>.<br />
              On s'occupe de TOUT pour toi en 7 jours. Tu arrives, tu travailles.
            </motion.p>
            
            {/* Stats */}
            <motion.div 
              className="stats"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="stat">
                <div className="v orange">-50%</div>
                <div className="l">Pas de porte</div>
              </div>
              <div className="stat">
                <div className="v green">-50%</div>
                <div className="l">Loyer à vie</div>
              </div>
              <div className="stat">
                <div className="v green">7 jours</div>
                <div className="l">Livraison</div>
              </div>
            </motion.div>
            
            {/* CTA */}
            <motion.div 
              className="ctas"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <a 
                href={CALENDLY_URL} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="cta-primary"
                data-testid="hero-cta-button"
              >
                <Crown size={20} />
                Réserver ma place Fondateur
                <ArrowRight size={18} />
              </a>
            </motion.div>
            
            {/* Infos complémentaires */}
            <motion.div 
              className="sub-info"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <span><Calendar size={14} /> Ouverture 1er juillet</span>
              <span><Shield size={14} /> Satisfait ou remboursé 30 jours</span>
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION — Ce qu'on fait pour toi (Paramétrage)
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="section-white">
          <div className="container">
            <div className="section-head">
              <span className="eyebrow eyebrow-green">
                <span className="dot" />
                Paramétrage clé en main
              </span>
              <h2 className="t-h2">
                En 7 jours, <span className="accent">ton business est prêt.</span>
              </h2>
              <p className="t-lead">
                Tu n'as pas le temps de tout configurer. Nous si.<br />
                On s'occupe de TOUT. Tu n'as plus qu'à vendre.
              </p>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 16,
            }}>
              {PARAMETRAGE.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    padding: '20px 24px',
                    background: C.cream,
                    borderRadius: 16,
                    border: `1px solid ${C.gray200}`,
                  }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: 'rgba(34, 197, 94, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Icon size={24} color={C.green} />
                    </div>
                    <span style={{ fontSize: 15, fontWeight: 600, color: C.navy }}>
                      {item.text}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION — Avantages Fondateur
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="section-light">
          <div className="container">
            <div className="section-head">
              <span className="eyebrow eyebrow-green">
                <span className="dot" />
                Avantages exclusifs
              </span>
              <h2 className="t-h2">
                Ce que tu obtiens en devenant <span className="accent">Fondateur</span>
              </h2>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: 20,
            }}>
              {[
                { icon: Gift, title: '5 ans offerts', desc: 'Pour les 100 premiers Membres Fondateurs uniquement', color: C.green },
                { icon: Percent, title: '-50% sur le pas de porte', desc: 'Paramétrage clé en main à moitié prix', color: C.orange },
                { icon: Sparkles, title: '-50% à vie sur le loyer', desc: 'Pour toujours, tant que tu restes locataire', color: C.green },
                { icon: Crown, title: 'Badge Fondateur exclusif', desc: 'Visible sur ton profil et ta boutique', color: '#F59E0B' },
                { icon: Users, title: 'Groupement des Fondateurs', desc: 'Fais partie d\'un groupe exclusif de pionniers', color: C.navy },
                { icon: Video, title: 'Webinaire trimestriel', desc: 'Un webinaire par trimestre entre Membres Fondateurs', color: '#3B82F6' },
                { icon: Rocket, title: 'Client pilote privilégié', desc: 'Teste en avant-première toutes les nouvelles fonctionnalités', color: '#EC4899' },
                { icon: Eye, title: '-50% boutique immersive 3D', desc: 'Sur la future création de ta boutique 3D', color: '#8B5CF6' },
              ].map((av, i) => {
                const Icon = av.icon;
                return (
                  <div key={i} style={{
                    background: '#fff',
                    borderRadius: 20,
                    padding: 28,
                    border: `1px solid ${C.gray200}`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                  }}>
                    <div style={{
                      width: 56,
                      height: 56,
                      borderRadius: 14,
                      background: `${av.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 20,
                    }}>
                      <Icon size={28} color={av.color} />
                    </div>
                    <h3 style={{ 
                      fontSize: 18, 
                      fontWeight: 700, 
                      color: C.navy, 
                      margin: 0,
                      marginBottom: 8,
                    }}>
                      {av.title}
                    </h3>
                    <p style={{ 
                      fontSize: 14, 
                      color: C.gray500, 
                      margin: 0,
                      lineHeight: 1.5,
                    }}>
                      {av.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION — Tarifs
        ═══════════════════════════════════════════════════════════════════ */}
        <section id="tarifs" className="section-white">
          <div className="container">
            <div className="section-head">
              <span className="eyebrow eyebrow-green">
                <span className="dot" />
                Tarifs Fondateur
              </span>
              <h2 className="t-h2">
                Choisis ta <span className="accent">formule</span>
              </h2>
              <p className="t-lead">
                -50% sur le pas de porte et -50% à vie sur le loyer
              </p>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 24,
              marginBottom: 40,
            }}>
              {FORMULES.map((formule) => (
                <div
                  key={formule.id}
                  onClick={() => setSelectedPlan(formule.id)}
                  data-testid={`pricing-card-${formule.id}`}
                  style={{
                    background: '#fff',
                    borderRadius: 24,
                    padding: formule.highlight ? 32 : 28,
                    border: selectedPlan === formule.id 
                      ? `3px solid ${C.green}` 
                      : formule.highlight 
                        ? `2px solid ${C.green}` 
                        : `1px solid ${C.gray200}`,
                    boxShadow: selectedPlan === formule.id 
                      ? '0 20px 50px rgba(34, 197, 94, 0.15)' 
                      : '0 4px 20px rgba(0,0,0,0.05)',
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                    position: 'relative',
                    transform: formule.highlight ? 'scale(1.02)' : 'scale(1)',
                  }}
                >
                  {/* Badge */}
                  {formule.badge && (
                    <div style={{
                      position: 'absolute',
                      top: -14,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDark} 100%)`,
                      color: '#fff',
                      padding: '6px 16px',
                      borderRadius: 50,
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: '0.05em',
                      boxShadow: '0 4px 15px rgba(34, 197, 94, 0.4)',
                    }}>
                      {formule.badge}
                    </div>
                  )}

                  {/* Check sélectionné */}
                  {selectedPlan === formule.id && (
                    <div style={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: C.green,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Check size={16} color="#fff" strokeWidth={3} />
                    </div>
                  )}

                  {/* Nom */}
                  <div style={{ marginBottom: 20, paddingTop: formule.badge ? 8 : 0 }}>
                    <h3 style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 28,
                      fontWeight: 800,
                      color: C.navy,
                      margin: 0,
                    }}>
                      Formule {formule.name}
                    </h3>
                  </div>

                  {/* Pas de porte */}
                  <div style={{
                    background: C.cream,
                    borderRadius: 14,
                    padding: 18,
                    marginBottom: 14,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.gray500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Pas de porte (paramétrage)
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                      <span style={{ fontSize: 15, color: C.gray500, textDecoration: 'line-through' }}>
                        {formule.pasDePorte.normal}€
                      </span>
                      <span style={{ fontSize: 36, fontWeight: 800, color: C.orange }}>
                        {formule.pasDePorte.fondateur}€
                      </span>
                      <span style={{
                        background: C.green,
                        color: '#fff',
                        padding: '3px 8px',
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 700,
                      }}>-50%</span>
                    </div>
                  </div>

                  {/* Loyer */}
                  <div style={{
                    background: 'rgba(34, 197, 94, 0.08)',
                    border: `1px solid rgba(34, 197, 94, 0.2)`,
                    borderRadius: 14,
                    padding: 18,
                    marginBottom: 20,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.gray500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Loyer mensuel <span style={{ color: C.green, fontWeight: 700 }}>à vie</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                      <span style={{ fontSize: 15, color: C.gray500, textDecoration: 'line-through' }}>
                        {formule.loyer.normal}€
                      </span>
                      <span style={{ fontSize: 32, fontWeight: 800, color: C.green }}>
                        {formule.loyer.fondateur}€
                      </span>
                      <span style={{ fontSize: 14, color: C.gray500 }}>/mois</span>
                    </div>
                  </div>

                  {/* Inclus */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {formule.inclus.map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <Check size={18} color={C.green} style={{ marginTop: 2, flexShrink: 0 }} />
                        <span style={{ fontSize: 14, color: C.gray600 }}>{item}</span>
                      </div>
                    ))}
                    {formule.nonInclus.map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <X size={18} color={C.gray500} style={{ marginTop: 2, flexShrink: 0 }} />
                        <span style={{ fontSize: 14, color: C.gray500 }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Récap + CTA */}
            <div style={{
              background: `linear-gradient(135deg, ${C.navyDeep} 0%, ${C.navy} 100%)`,
              borderRadius: 24,
              padding: 'clamp(28px, 4vw, 40px)',
              textAlign: 'center',
            }}>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, margin: 0, marginBottom: 8 }}>
                Tu as sélectionné
              </p>
              <h3 style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 'clamp(24px, 3vw, 32px)',
                fontWeight: 800,
                color: '#fff',
                margin: 0,
                marginBottom: 20,
              }}>
                Formule {FORMULES.find(f => f.id === selectedPlan)?.name} Fondateur
              </h3>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: 40, 
                marginBottom: 28,
                flexWrap: 'wrap',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 32, fontWeight: 800, color: C.orange }}>
                    {FORMULES.find(f => f.id === selectedPlan)?.pasDePorte.fondateur}€
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Pas de porte</div>
                </div>
                <div style={{ width: 1, background: 'rgba(255,255,255,0.2)' }} />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 32, fontWeight: 800, color: C.green }}>
                    {FORMULES.find(f => f.id === selectedPlan)?.loyer.fondateur}€<span style={{ fontSize: 14, fontWeight: 500 }}>/mois</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Loyer à vie</div>
                </div>
              </div>

              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calendly-cta-button"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '18px 40px',
                  background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDark} 100%)`,
                  color: '#fff',
                  borderRadius: 14,
                  fontWeight: 700,
                  fontSize: 17,
                  textDecoration: 'none',
                  boxShadow: '0 8px 28px rgba(34, 197, 94, 0.4)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
              >
                <Crown size={20} />
                Réserver ma place maintenant
                <ArrowRight size={18} />
              </a>
              
              <div style={{ 
                marginTop: 20,
                display: 'flex',
                justifyContent: 'center',
                gap: 24,
                flexWrap: 'wrap',
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
                  <Shield size={14} /> Satisfait ou remboursé 30 jours
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
                  <Lock size={14} /> Paiement 100% sécurisé
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION — Bonus Boutique 3D
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="section-light">
          <div className="container">
            <div style={{
              background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
              borderRadius: 24,
              padding: 'clamp(32px, 5vw, 48px)',
              display: 'flex',
              alignItems: 'center',
              gap: 32,
              flexWrap: 'wrap',
            }}>
              <div style={{
                width: 72,
                height: 72,
                borderRadius: 18,
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Eye size={36} color="#fff" />
              </div>
              <div style={{ flex: 1, minWidth: 280 }}>
                <div style={{
                  display: 'inline-block',
                  background: 'rgba(255,255,255,0.2)',
                  padding: '4px 12px',
                  borderRadius: 50,
                  marginBottom: 12,
                }}>
                  <span style={{ color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em' }}>
                    BONUS FONDATEUR
                  </span>
                </div>
                <h3 style={{ 
                  color: '#fff', 
                  fontSize: 'clamp(22px, 3vw, 28px)', 
                  fontWeight: 800, 
                  margin: 0,
                  marginBottom: 12,
                }}>
                  -50% sur la boutique immersive 3D
                </h3>
                <p style={{ 
                  color: 'rgba(255,255,255,0.85)', 
                  fontSize: 15, 
                  lineHeight: 1.65,
                  margin: 0,
                }}>
                  Bientôt, ton client pourra entrer dans ta boutique comme dans un jeu vidéo. 
                  En tant que Fondateur, tu bénéficies de -50% sur la création de ta boutique immersive.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            CTA FINAL
        ═══════════════════════════════════════════════════════════════════ */}
        <section style={{ 
          padding: '80px 24px',
          background: `linear-gradient(135deg, ${C.navyDeep} 0%, ${C.navy} 100%)`,
          textAlign: 'center',
        }}>
          <div style={{ maxWidth: 700, margin: '0 auto' }}>
            <Crown size={56} color={C.green} style={{ marginBottom: 24 }} />
            
            <h2 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 'clamp(28px, 4vw, 42px)',
              fontWeight: 800,
              color: '#fff',
              margin: 0,
              marginBottom: 16,
            }}>
              Rejoins les <span style={{ color: C.green }}>100 premiers</span>
            </h2>
            
            <p style={{
              fontSize: 17,
              color: 'rgba(255,255,255,0.75)',
              marginBottom: 32,
              lineHeight: 1.65,
            }}>
              L'offre Fondateur est <strong style={{ color: '#fff' }}>limitée</strong>. 
              Une fois les 100 places prises, les tarifs reviendront à la normale.
            </p>

            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-cta-button"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                padding: '20px 48px',
                background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDark} 100%)`,
                color: '#fff',
                borderRadius: 16,
                fontWeight: 700,
                fontSize: 18,
                textDecoration: 'none',
                boxShadow: '0 8px 32px rgba(34, 197, 94, 0.4)',
              }}
            >
              <Crown size={22} />
              Devenir Membre Fondateur
              <ArrowRight size={20} />
            </a>
          </div>
        </section>

      </div>
    </ImmersiveEspaceProWrapper>
  );
}
